import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '../analytics';

// "Respond" to a prompt asks how: a post (one song or album) or a list. Every
// respond button (the prompt page, the prompts list, the feed's prompt card)
// opens this same menu under itself, so the choice reads the same wherever it
// starts. Styled like the post menus: white, offset black border.

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1100;
`;

const Menu = styled.div`
  position: fixed;
  background: white;
  color: black;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 8px;
  padding: 4px 0;
  z-index: 1101;
  min-width: 220px;
  text-align: left;
`;

const Item = styled.button`
  display: block;
  width: 100%;
  min-height: 44px;
  background: none;
  border: none;
  padding: 0.55em 0.9em;
  color: black;
  font-family: inherit;
  text-align: left;
  cursor: pointer;

  & + & {
    border-top: 1px solid #eee;
  }

  &:hover,
  &:focus-visible {
    background: #f0f0f0;
  }
`;

const ItemLabel = styled.div`
  font-weight: 700;
  font-size: 0.85rem;
`;

const ItemDesc = styled.div`
  font-size: 0.72rem;
  color: #666;
  margin-top: 0.1em;
`;

interface Anchor {
  promptId: string;
  trigger: HTMLElement;
  left: number;
  top: number;
  bottom: number;
}

/**
 * Returns `open`, for a respond button's onClick, `triggerProps`, the same
 * plus the menu ARIA state for a button to spread, and `chooser`, the menu to
 * render anywhere in the same component. `onChosen` runs once a choice is made
 * (the feed's prompt card dismisses itself then, not when the menu merely
 * opens).
 */
export function usePromptResponseChooser(onChosen?: (promptId: string) => void) {
  var navigate = useNavigate();
  var _anchor = useState<Anchor | null>(null);
  var anchor = _anchor[0];
  var setAnchor = _anchor[1];
  var menuRef = useRef<HTMLDivElement | null>(null);

  // Closing hands focus back to the button that opened the menu.
  function close(returnFocus: boolean) {
    var trigger = anchor && anchor.trigger;
    setAnchor(null);
    if (returnFocus && trigger) trigger.focus();
  }

  // Placed and focused once per opening, not on every render of the page
  // around it: the feed re-renders often, and each pass pulled focus back.
  useLayoutEffect(
    function () {
      var el = menuRef.current;
      if (!anchor || !el) return;
      // Under the button, kept on screen; above it when there is no room.
      var w = el.offsetWidth || 220;
      var h = el.offsetHeight || 110;
      var left = Math.max(8, Math.min(anchor.left, window.innerWidth - w - 8));
      var top = anchor.bottom + 4;
      if (top + h > window.innerHeight - 8) top = anchor.top - h - 4;
      el.style.left = left + 'px';
      el.style.top = Math.max(8, top) + 'px';
      var first = el.querySelector('button');
      // Without scrolling: a scroll closes the menu (below).
      if (first) (first as HTMLButtonElement).focus({ preventScroll: true });
    },
    [anchor]
  );

  // A fixed menu would drift away from its button as the page scrolls, so it
  // closes instead, as it does on Escape.
  useEffect(
    function () {
      if (!anchor) return;
      function onKey(e: KeyboardEvent) {
        if (e.key === 'Escape') close(true);
      }
      function onMove() {
        setAnchor(null);
      }
      window.addEventListener('keydown', onKey);
      window.addEventListener('scroll', onMove, true);
      window.addEventListener('resize', onMove);
      return function () {
        window.removeEventListener('keydown', onKey);
        window.removeEventListener('scroll', onMove, true);
        window.removeEventListener('resize', onMove);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [anchor]
  );

  function open(e: React.MouseEvent<HTMLElement>, promptId: string) {
    e.preventDefault();
    var trigger = e.currentTarget;
    var rect = trigger.getBoundingClientRect();
    setAnchor({
      promptId: promptId,
      trigger: trigger,
      left: rect.left,
      top: rect.top,
      bottom: rect.bottom,
    });
  }

  function triggerProps(promptId: string) {
    return {
      onClick: function (e: React.MouseEvent<HTMLElement>) {
        open(e, promptId);
      },
      'aria-haspopup': 'menu' as const,
      'aria-expanded': !!anchor && anchor.promptId === promptId,
    };
  }

  function choose(kind: 'post' | 'list') {
    if (!anchor) return;
    var promptId = anchor.promptId;
    setAnchor(null);
    trackEvent('prompt_response_chosen', { prompt_id: promptId, kind: kind });
    if (onChosen) onChosen(promptId);
    navigate((kind === 'post' ? '/post' : '/create-list') + '?promptId=' + promptId);
  }

  // Up and Down move between the two choices; Tab out of the menu closes it.
  function onMenuKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    var items = Array.prototype.slice.call(e.currentTarget.querySelectorAll('button'));
    var i = items.indexOf(document.activeElement);
    var next =
      e.key === 'ArrowDown' ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
    items[next].focus();
  }
  // Closes only when focus moves to something else (Tab out). A blur with no
  // new target is not leaving: Safari does not focus a button on tap, so
  // tapping a choice blurs the focused one with nothing after it, and closing
  // then threw the tap away. Taps outside the menu land on the overlay.
  function onMenuBlur(e: React.FocusEvent<HTMLDivElement>) {
    var next = e.relatedTarget as Node | null;
    if (next && !e.currentTarget.contains(next)) setAnchor(null);
  }

  var chooser = anchor ? (
    <>
      <Overlay
        onClick={function () {
          close(false);
        }}
      />
      <Menu role="menu" ref={menuRef} onKeyDown={onMenuKeyDown} onBlur={onMenuBlur}>
        <Item
          role="menuitem"
          onClick={function () {
            choose('post');
          }}
        >
          <ItemLabel>Post</ItemLabel>
          <ItemDesc>One song or album</ItemDesc>
        </Item>
        <Item
          role="menuitem"
          onClick={function () {
            choose('list');
          }}
        >
          <ItemLabel>List</ItemLabel>
          <ItemDesc>A ranked list or a playlist</ItemDesc>
        </Item>
      </Menu>
    </>
  ) : null;

  return { open: open, triggerProps: triggerProps, chooser: chooser };
}
